import { defineRule } from "vee-validate";

import {
  required,
  email,
  min,
  max,
  confirmed,
  alpha_spaces
} from "@vee-validate/rules";
import { defineNuxtPlugin } from "nuxt/app";
import { isValidRut, normalizeRut } from "~/utils/documentNumber";

export default defineNuxtPlugin((nuxtApp) => {
  const i18n = nuxtApp.$i18n as {
    t: (key: string, params?: Record<string, unknown>) => string;
  };
  const t = (key: string, params?: Record<string, unknown>) =>
    i18n.t(key, params);

  defineRule("required", (value: string) => {
    if (required(value)) {
      return true;
    }
    return t("validation.required");
  });
  defineRule("email", (value: string) => {
    if (!value) {
      return t("validation.emailRequired");
    }
    if (email(value)) {
      return true;
    }
    return t("validation.email");
  });
  // Same email format check, but for optional fields — an empty value is
  // valid (unlike "email", which treats blank as a required-field failure).
  defineRule("optional_email", (value: string) => {
    if (!value) {
      return true;
    }
    if (email(value)) {
      return true;
    }
    return t("validation.email");
  });
  defineRule("min", (value: string, [limit]: [number], ctx) => {
    if (min(value, [limit])) {
      return true;
    }
    const field = ctx.field;
    const fieldTranslations = {
      password: t("validation.fields.password"),
      passwordRepeat: t("validation.fields.password"),
      email: t("validation.fields.email"),
      names: t("validation.fields.names"),
      lastnames: t("validation.fields.lastnames"),
      gender: t("validation.fields.gender"),
      birthdate: t("validation.fields.birthdate"),
      educationLevel: t("validation.fields.educationLevel")
    };
    const fieldName =
      fieldTranslations[field as keyof typeof fieldTranslations] || field;

    return t("validation.min", {
      field: fieldName,
      min: limit || t("validation.defaultMin")
    });
  });
  defineRule("max", max);

  // The only caller of this rule is DesignSystemFormInputDateField, which
  // binds field.value straight to a b-datepicker — the value validated here
  // is always a Date (or null), never the "DD/MM/YYYY" string the [param]
  // name implies. Validate that shape instead of a format that never arrives.
  defineRule("date_format", (value: Date | null | undefined) => {
    if (value instanceof Date && !isNaN(value.getTime())) {
      return true;
    }
    return t("validation.dateFormat");
  });
  // No callers today, but it sits next to date_format — the rule this task
  // fixed precisely because it assumed a string and was always handed a
  // Date. Accept both shapes here too instead of leaving the same trap.
  defineRule("valid_date_range", (value: Date | string | null | undefined) => {
    const inputDate =
      value instanceof Date
        ? value
        : typeof value === "string" && value
          ? new Date(value.split("/").reverse().join("-"))
          : null;

    if (!inputDate || isNaN(inputDate.getTime())) {
      return t("validation.invalidDate");
    }

    const today = new Date();
    const minDate = new Date(
      today.getFullYear() - 100,
      today.getMonth(),
      today.getDate()
    );

    if (inputDate > today) {
      return t("validation.futureDate");
    }

    if (inputDate < minDate) {
      return t("validation.dateTooOld");
    }

    return true;
  });
  defineRule("two_last_name", (value: string | null) => {
    if (!value || value.trim() === "") {
      return false;
    }
    const words = value.trim().split(/\s+/);

    if (words.length !== 2) {
      return t("validation.twoLastName");
    }

    const isValidWord = (word: string) =>
      /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ]+$/.test(word);
    if (!isValidWord(words[0] || "") || !isValidWord(words[1] || "")) {
      return t("validation.lastNamesLetters");
    }

    return true;
  });
  defineRule("same_password", (value: string, [target]: [string]) => {
    if (confirmed(value, [target])) {
      return true;
    }
    return t("validation.samePassword");
  });
  defineRule("same_email", (value: string, [target]: [string]) => {
    if (confirmed(value, [target])) {
      return true;
    }
    return t("validation.sameEmail");
  });
  defineRule("alpha_spaces", (value: string) => {
    if (alpha_spaces(value, {})) {
      return true;
    }
    return t("validation.alphaSpaces");
  });
  defineRule("rut", (value: string) => {
    if (!value) {
      return t("validation.documentNumberRequired");
    }
    if (isValidRut(normalizeRut(value))) {
      return true;
    }
    return t("validation.documentNumber");
  });
  defineRule("required_autocomplete_input", (value: string) => {
    if (required(value)) {
      return true;
    }
    return t("validation.requiredAutocomplete");
  });
  defineRule("required_search", (value: string) => {
    if (required(value)) {
      return true;
    }
    return t("validation.requiredSearch");
  });
  defineRule("required_search_occupation", (value: string) => {
    if (required(value)) {
      return true;
    }
    return t("validation.requiredSearchOccupation");
  });
  defineRule(
    "different",
    (value: string, [target, message]: [string, string?]) => {
      if (value !== target) {
        return true;
      }
      return message ?? t("validation.different");
    }
  );
  defineRule("min_age", (value: string, [years]: [number]) => {
    if (!value) return true;
    const [day, month, year] = value.split("/").map(Number);
    const birthdate = new Date(year!, month! - 1, day);
    if (isNaN(birthdate.getTime())) return true;
    const today = new Date();
    const minBirthdate = new Date(
      today.getFullYear() - years,
      today.getMonth(),
      today.getDate()
    );
    if (birthdate <= minBirthdate) {
      return true;
    }
    return t("validation.minAge", { years });
  });
});
